import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import './ScrollFloat.css';

gsap.registerPlugin(ScrollTrigger);

const ScrollFloat = ({
  children,
  scrollContainerRef,
  containerClassName = '',
  textClassName = '',
  animationDuration = 1,
  ease = 'back.inOut(2)',
  scrollStart = 'center bottom+=50%',
  scrollEnd = 'bottom bottom-=40%',
  stagger = 0.03,
  scrub = true,
  as: As = 'h2',
}) => {
  const containerRef = useRef(null);

  const splitText = useMemo(() => {
    const text = typeof children === 'string' ? children : '';
    return text.split('').map((char, index) => (
      <span className="char" key={index}>
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  }, [children]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Respect reduced motion preferences
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const scroller = scrollContainerRef && scrollContainerRef.current
      ? scrollContainerRef.current
      : window;

    const charElements = el.querySelectorAll('.char');

    if (!charElements || charElements.length === 0) return;

    if (prefersReduced) {
      gsap.set(charElements, { opacity: 1, yPercent: 0, scaleY: 1, scaleX: 1 });
      return;
    }

    const tween = gsap.fromTo(
      charElements,
      {
        willChange: 'opacity, transform',
        opacity: 0,
        yPercent: 120,
        scaleY: 2.3,
        scaleX: 0.7,
        transformOrigin: '50% 0%'
      },
      {
        duration: animationDuration,
        ease: ease,
        opacity: 1,
        yPercent: 0,
        scaleY: 1,
        scaleX: 1,
        stagger: stagger,
        scrollTrigger: {
          trigger: el,
          scroller,
          start: scrollStart,
          end: scrollEnd,
          scrub: scrub
        }
      }
    );

    return () => {
      try {
        tween?.scrollTrigger?.kill?.();
        tween?.kill?.();
      } catch {
        /* noop */
      }
    };
  }, [scrollContainerRef, animationDuration, ease, scrollStart, scrollEnd, stagger, scrub]);

  return (
    <As ref={containerRef} className={`scroll-float ${containerClassName}`}>
      <span className={`scroll-float-text ${textClassName}`}>{splitText}</span>
    </As>
  );
};

ScrollFloat.propTypes = {
  children: PropTypes.node,
  scrollContainerRef: PropTypes.shape({ current: PropTypes.any }),
  containerClassName: PropTypes.string,
  textClassName: PropTypes.string,
  animationDuration: PropTypes.number,
  ease: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  scrollStart: PropTypes.string,
  scrollEnd: PropTypes.string,
  stagger: PropTypes.number,
  scrub: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
};

export default ScrollFloat;
